import React, { useCallback, useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import { v4 as uuid } from 'uuid';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { getRegions } from '@luxuryescapes/lib-regions';

import {
  InternalPromoWithDiscounts,
  PromoLoyaltyMetaConfig,
  PromoMeta,
  PromoProductTypes,
  PromoRegionGroupings,
  getMinSpendProductTypeOptions,
  getPromoTypeDisplayName,
  promoTypesForSubscription,
} from '~/services/PromoService';

import { sortBy } from '~/utils/arrayUtils';
import { getCurrencyCodeByRegionCode } from '~/utils/getCurrencyCodeByRegionCode';

import LoyaltyPointsBonusConfig from './LoyaltyPointsBonusConfig';
import StandaloneMultiProductOptionSelect from './StandaloneMultiProductOptionSelect';

const SUBSCRIPTION_DISCOUNT_TYPE_TOOLTIP =
  'Applies an extra discount only to subscription items, such as membership joining or recurring fees. Choose Percentage for % off those items, Fixed Amount for a currency amount off, or None to leave subscription items undiscounted. This is separate from the main product discount and does not count toward Max Discount.';

/**
 * @deprecated (define in backend)
 */
const REGION_SORT_ORDER = [
  'Australia',
  'New Zealand',
  'United States',
  'United Kingdom',
  'Singapore',
  'India',
  'Germany',
  'Italy',
  'Canada',
  'China',
  'France',
  'Hong Kong',
  'Indonesia',
  'Ireland',
  'Israel',
  'Japan',
  'Korea',
  'Macau',
  'Malaysia',
  'Netherlands',
  'Philippines',
  'Qatar',
  'Russia',
  'Saudi Arabia',
  'South Africa',
  'Spain',
  'Taiwan',
  'Thailand',
  'United Arab Emirates',
  'Vietnam',
];

type RegionOption = {
  name: string;
  code: string;
  message?: string;
};

const getRegionOptions = (extraRegionOptions: PromoRegionGroupings): Array<RegionOption> => {
  return extraRegionOptions
    .map(
      (region): RegionOption => ({
        name: region.code,
        code: region.code,
        message: `${region.code} - includes ${region.regionCodes.join(', ')}`,
      }),
    )
    .concat(
      getRegions().map(
        (region): RegionOption => ({
          name: region.name,
          code: region.code,
        }),
      ),
    );
};

const getSortedRegions = (extraRegionOptions: PromoRegionGroupings) =>
  sortBy(
    getRegionOptions(extraRegionOptions),
    (region) => {
      const regionOrder = REGION_SORT_ORDER.indexOf(region.name);
      if (regionOrder === -1) {
        return 999;
      }
      return regionOrder;
    },
    'asc',
  );

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (discount: App.PromoDiscount) => void;
  discount: App.PromoDiscount | null;
  isPromoMetaLoading: boolean;
  promoLoyaltyBonusApplyMethod: PromoLoyaltyMetaConfig['loyaltyBonusFilterMethods'][number]['key'] | null;
  isNew: boolean;
  promoType: string;
  promoProductTypes: PromoProductTypes;
  promoRegionGroupings: PromoRegionGroupings;
  promoTypes: PromoMeta['promo_types'];
  promoLoyaltyMetaConfig: PromoLoyaltyMetaConfig;
  promoCode: InternalPromoWithDiscounts | null;
}

function PromoDiscountEditModal({
  open,
  onClose,
  onSave,
  discount,
  isNew,
  promoType,
  promoProductTypes,
  promoRegionGroupings,
  promoTypes,
  promoLoyaltyMetaConfig,
  isPromoMetaLoading,
  promoLoyaltyBonusApplyMethod,
  promoCode,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const [formData, setFormData] = useState<App.PromoDiscount | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [promoDiscountLoyaltyBonus, setPromoDiscountLoyaltyBonus] =
    useState<App.PromoDiscount['loyalty_points_bonus_config']>(promoLoyaltyBonusApplyMethod);

  const regionsOptions = getSortedRegions(promoRegionGroupings);

  const handleLoyaltyBonusConfigChange = useCallback((newConfig) => {
    setPromoDiscountLoyaltyBonus(newConfig);
    setFormData((prev) => (prev ? { ...prev, loyalty_points_bonus_config: newConfig } : null));
  }, []);

  useEffect(() => {
    if (discount) {
      setFormData({ ...discount });
      setPromoDiscountLoyaltyBonus(discount.loyalty_points_bonus_config);
    } else {
      const id = uuid();

      const allMinSpendProductOptions = getMinSpendProductTypeOptions(promoProductTypes, []);
      const defaultMinSpendProducts = allMinSpendProductOptions.map((option) => option.value);

      setFormData({
        id,
        id_promo_code: promoCode?.id_promo_code,
        id_promo_code_discount: id,
        products: ['ALL-ALL'],
        region: 'AU',
        subscription_item_discount_type: 'none',
        isNew: true,
        min_spend_products: defaultMinSpendProducts,
        discount_value: 0,
        max_discount: 0,
        min_spend: 0,
        subscription_item_discount_value: 0,
        min_user_used_limit: 0,
        modified_by: '',
        created_at: '',
        updated_at: '',
        loyalty_points_bonus_config: undefined,
      });
      setPromoDiscountLoyaltyBonus(null);
    }
  }, [discount, promoCode?.id_promo_code, promoProductTypes]);

  const handleInputChange = useCallback((field: keyof App.PromoDiscount, value: any) => {
    setFormData((prev) => (prev ? { ...prev, [field]: value } : null));
  }, []);

  const handleSave = useCallback(async () => {
    if (!formData) {
      enqueueSnackbar('No form data to save', { variant: 'error' });
      return;
    }

    setIsSubmitting(true);
    try {
      const updatedFormData = {
        ...formData,
        loyalty_points_bonus_config: promoDiscountLoyaltyBonus,
      };
      await onSave(updatedFormData);
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, promoDiscountLoyaltyBonus, onSave]);

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      onClose();
    }
  }, [isSubmitting, onClose]);

  if (!formData) {
    return null;
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{isNew ? 'Add New Discount' : 'Edit Discount'}</DialogTitle>
      <DialogContent>
        <Stack spacing={3}>
          <Stack>
            <Typography variant="h5">Reward:</Typography>

            <Typography variant="caption" color="text.secondary">
              The reward to be applied to the order if the requirements are met.
            </Typography>
          </Stack>
          <FormControl fullWidth>
            <InputLabel>Customer Region</InputLabel>
            <Select
              value={formData.region}
              label="Customer Region"
              onChange={(e) => handleInputChange('region', e.target.value)}
            >
              {regionsOptions.map((region) => (
                <MenuItem key={region.code} value={region.code} title={region.message}>
                  {region.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Stack direction="row" spacing={3}>
            <TextField
              sx={{ flex: 1 }}
              label={`${getPromoTypeDisplayName(promoType)} Discount Value`}
              type="number"
              value={formData.discount_value || ''}
              onChange={(e) => handleInputChange('discount_value', parseFloat(e.target.value))}
              inputProps={{ min: 0, step: 0.01 }}
            />

            <FormControl sx={{ flex: 1 }}>
              <StandaloneMultiProductOptionSelect
                id="products-select"
                multiSelectType="products"
                value={formData.products || []}
                promoProductTypes={promoProductTypes}
                onChange={(value) => handleInputChange('products', value)}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                The products this discount applies to
              </Typography>
            </FormControl>
          </Stack>

          {promoType === 'percentage' && (
            <TextField
              fullWidth
              label="Max Discount"
              type="number"
              value={formData.max_discount || ''}
              onChange={(e) =>
                handleInputChange('max_discount', e.target.value === '' ? 0 : parseFloat(e.target.value) || 0)
              }
              inputProps={{ min: 0, step: 0.01 }}
              helperText="The maximum amount of discounting that can be provided by this discount - does not apply to subscription item discounts"
            />
          )}

          <Stack direction="row" spacing={3}>
            <FormControl sx={{ flex: 1 }}>
              <Stack direction="row" alignItems="center" gap={0.5} mb={1}>
                <Typography variant="caption" color="text.secondary">
                  Subscription Discount Type
                </Typography>
                <Tooltip title={SUBSCRIPTION_DISCOUNT_TYPE_TOOLTIP}>
                  <InfoOutlinedIcon fontSize="small" sx={{ cursor: 'help', color: 'text.secondary' }} />
                </Tooltip>
              </Stack>
              <Select
                value={formData.subscription_item_discount_type}
                onChange={(e) => handleInputChange('subscription_item_discount_type', e.target.value)}
              >
                <MenuItem value="none">None</MenuItem>
                {promoTypesForSubscription(promoTypes).map((type) => (
                  <MenuItem key={type.key} value={type.key}>
                    {type.description}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {formData.subscription_item_discount_type !== 'none' && (
              <TextField
                sx={{ flex: 1 }}
                label="Subscription Discount Value"
                type="number"
                value={formData.subscription_item_discount_value}
                onChange={(e) => handleInputChange('subscription_item_discount_value', parseFloat(e.target.value) || 0)}
                inputProps={{
                  min: 1,
                  step: formData.subscription_item_discount_type === 'percentage' ? 0.01 : 0.01,
                }}
                helperText={`Discount for subscription items - will not apply contribute to max discount checks${
                  formData.subscription_item_discount_type === 'percentage'
                    ? ' (as percentage)'
                    : formData.subscription_item_discount_type === 'fixed_amount'
                      ? ` (in ${getCurrencyCodeByRegionCode(formData.region) || 'AUD'})`
                      : ` (as ${getPromoTypeDisplayName(formData.subscription_item_discount_type).toLowerCase()})`
                }`}
              />
            )}
          </Stack>
          <LoyaltyPointsBonusConfig
            loyaltyBonusApplyMethod={promoLoyaltyBonusApplyMethod}
            isPromoMetaLoading={isPromoMetaLoading}
            promoLoyaltyMetaConfig={promoLoyaltyMetaConfig}
            config={promoDiscountLoyaltyBonus}
            onConfigChange={handleLoyaltyBonusConfigChange}
          />

          <Divider />
          <Stack>
            <Typography variant="h5">Requirements:</Typography>

            <Typography variant="caption" color="text.secondary">
              Requirements are to be met for the discount to be applied.
            </Typography>
          </Stack>
          <Stack direction="row" spacing={3}>
            <TextField
              label="Min Spend"
              type="number"
              value={formData.min_spend || ''}
              onChange={(e) =>
                handleInputChange('min_spend', e.target.value === '' ? 0 : parseFloat(e.target.value) || 0)
              }
              inputProps={{ min: 0, step: 0.01 }}
              helperText="Only orders with this level of spending will qualify for this discount"
            />

            <FormControl>
              <StandaloneMultiProductOptionSelect
                id="min-spend-products-select"
                multiSelectType="min_spend_products"
                value={formData.min_spend_products || []}
                promoProductTypes={promoProductTypes}
                onChange={(value) => handleInputChange('min_spend_products', value)}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                When determining the minimum spend, only these product types will count when considering if the customer
                qualifies for the discount (default is all product types)
              </Typography>
            </FormControl>
          </Stack>

          <TextField
            fullWidth
            label="Min User Used Limit"
            type="number"
            value={formData.min_user_used_limit}
            onChange={(e) => handleInputChange('min_user_used_limit', parseInt(e.target.value) || 0)}
            inputProps={{ min: 0 }}
            helperText="This promo must be used this many times before a user qualifies for this discount"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default PromoDiscountEditModal;
